import { Component, Input } from '@angular/core';
import { EChartsOption } from 'echarts';
import { IncidentBreakdownGraph } from 'src/app/models/incident';

@Component({
  selector: 'app-incident-breakdown',
  templateUrl: './incident-breakdown.component.html',
  styleUrls: ['./incident-breakdown.component.scss'],
})
export class IncidentBreakdownComponent {
  @Input() set chartHeight(val: any) {
    if (val) this.height = val + 'px';
  }
  @Input() set breakdownIncidentData(val: IncidentBreakdownGraph[]) {
    if (val) {
      this.setChartData(val);
    }
  }
  height = '';
  chartOption: EChartsOption;

  private setChartData(val: IncidentBreakdownGraph[]) {
    const copiedDatesArray = val.slice();
    const sortedDate = copiedDatesArray.sort(
      (a, b) =>
        new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime()
    );

    const dates = sortedDate.map((item) =>
      new Date(item.eventDate).toLocaleString('en-US')
    );

    // Predefined incident types
    const staticIncidentTypes = [
      { key: 'intrusionCount', label: 'Intrusion' },
      { key: 'fireCount', label: 'Fire' },
      { key: 'securityCallbacksCount', label: 'Security Callbacks' },
      { key: 'soSAlertsCount', label: 'SoS Alerts' },
      { key: 'deviceFailureCount', label: 'Device Failure' },
      { key: 'claimsCount', label: 'Claims' },
    ];

    const series: any[] = [];
    const legendData: any[] = [];
    let combinedArray: number[] = [];

    // Add static incident types
    staticIncidentTypes.forEach(({ key, label }) => {
      const data = sortedDate.map(
        (item) => item[key as keyof IncidentBreakdownGraph] as number
      );
      if (data.some((count) => count > 0)) {
        series.push({ name: label, type: 'line', stack: key, data });
        legendData.push(label);
        combinedArray = combinedArray.concat(data);
      }
    });

    // Extract all unique manual incident keys
    const manualIncidentKeys = new Set<string>();
    sortedDate.forEach((item) => {
      if (item.manualIncidentTypeCounts) {
        Object.keys(item.manualIncidentTypeCounts).forEach((key) =>
          manualIncidentKeys.add(key)
        );
      }
    });

    // Add dynamic manual incident types
    manualIncidentKeys.forEach((incidentKey) => {
      const data = sortedDate.map(
        (item) => item.manualIncidentTypeCounts?.[incidentKey] ?? 0
      );
      if (data.some((count) => count > 0)) {
        series.push({
          name: incidentKey,
          type: 'line',
          stack: incidentKey,
          data,
        });
        legendData.push(incidentKey);
        combinedArray = combinedArray.concat(data);
      }
    });

    const maxVal = Math.max(...combinedArray, 0);

    // Enhanced legend configuration
    const legendConfig = this.getEnhancedLegendConfig(legendData.length);

    this.chartOption = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(50, 50, 50, 0.95)',
        borderColor: '#777',
        borderWidth: 1,
        textStyle: {
          color: '#fff',
          fontSize: 12,
        },
        // formatter: function (params: any) {
        //   const date = new Date(params[0].axisValue); // assumes axisValue is a date string or timestamp
        //   const day = String(date.getDate()).padStart(2, '0');
        //   const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-based
        //   const year = date.getFullYear();

        //   const formattedDate = `${day}/${month}/${year}`;

        //   let tooltipText = `${formattedDate}<br/>`;
        //   params.forEach((item: any) => {
        //     tooltipText += `${item.marker} ${item.seriesName}: ${item.value}<br/>`;
        //   });
        //   return tooltipText;
        // },
        formatter: function (params: any) {
          const date = new Date(params[0].axisValue);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();

          const formattedDate = `${day}/${month}/${year}`;

          let tooltipText = `${formattedDate}<br/>`;

          // Filter params to show only values > 0
          const filteredParams = params.filter((item: any) => item.value > 0);
          const zeroValueCount = params.length - filteredParams.length;

          // If no active incidents, show only no incidents message
          if (filteredParams.length === 0) {
            let tooltipText = `${formattedDate}<br/>`;
            tooltipText += `<span style="color: #95A5A6; font-style: italic;">No incidents recorded</span><br/>`;
            tooltipText += `<span style="color: #4A90E2; font-size: 11px; font-style: italic;">ℹ️ All ${params.length} incident types have zero occurrences</span>`;
            return tooltipText;
          }

          // Show active incidents (values > 0)
          if (filteredParams.length > 0) {
            filteredParams.forEach((item: any) => {
              tooltipText += `${item.marker} ${item.seriesName}: <strong>${item.value}</strong><br/>`;
            });
          }

          // Add informative note about zero values
          if (zeroValueCount > 0) {
            const noteText =
              zeroValueCount === 1
                ? `${zeroValueCount} other incident type has no occurrences`
                : `${zeroValueCount} other incident types have no occurrences`;

            tooltipText += `<br/><span style="color: #4A90E2; font-size: 11px; font-style: italic;">ℹ️ ${noteText}</span>`;
          }

          return tooltipText;
        },
      },

      legend: {
        data: legendData,
        ...legendConfig,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '8%',
        top: legendConfig.topMargin, // Dynamic top margin based on legend
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLabel: {
          rotate: dates.length > 8 ? 45 : 0,
          formatter: (value: string) => {
            const date = new Date(value);
            const month = date.toLocaleString('en-us', { month: 'short' });
            const day = date.getDate();
            return `${month} ${day}`;
          },
          fontSize: 11,
          color: '#666',
        },
        axisLine: {
          lineStyle: {
            color: '#e0e0e0',
          },
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: maxVal + Math.ceil(maxVal * 0.1), // 10% padding above max value
        axisLabel: {
          fontSize: 11,
          color: '#666',
        },
        axisLine: {
          lineStyle: {
            color: '#e0e0e0',
          },
        },
        splitLine: {
          lineStyle: {
            color: '#f0f0f0',
            type: 'dashed',
          },
        },
      },
      series: series.map((s, index) => ({
        ...s,
        smooth: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          borderWidth: 2,
          borderColor: '#fff',
        },
        emphasis: {
          focus: 'series',
          blurScope: 'coordinateSystem',
        },
      })),
      // --- Added dataZoom for scroll functionality ---

      dataZoom: [
        {
          type: 'slider', // Or 'inside' for direct dragging/pinching

          show: true,

          xAxisIndex: [0],

          bottom: 20, // Position it at the bottom

          height: 20, // Height of the dataZoom component

          start: 0, // Initial zoom start percentage

          end: dates.length > 7 ? (7 / dates.length) * 100 : 100, // Show max 7 data points initially, or all if less than 7

          handleIcon:
            'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-9,0.8-9,1.4v7.3c0,0.6,4.1,1.1,9,1.4v1.3h1.3v-1.3c4.9-0.3,9-0.8,9-1.4v-7.3C19.7,12.7,15.6,12.2,10.7,11.9z M2,14.1v5.6h16V14.1H2z',

          handleSize: '110%',

          handleStyle: {
            color: '#a7b7c7',
          },

          textStyle: {
            color: '#8392A5',
          },

          borderColor: '#eee',

          backgroundColor: '#f5f5f5',

          fillerColor: 'rgba(167, 183, 204, 0.4)',

          dataBackground: {
            lineStyle: {
              color: '#fff',
            },

            areaStyle: {
              color: '#dcdcdc',
            },
          },

          brushStyle: {
            borderColor: '#999',
          },
        },

        {
          type: 'inside', // For pinching and dragging within the chart area

          xAxisIndex: [0],

          start: 0,

          end: dates.length > 7 ? (7 / dates.length) * 100 : 100,
        },
      ],
      // Enhanced animation
      animation: true,
      animationDuration: 1000,
      animationEasing: 'cubicOut',
    };
  }

  private getEnhancedLegendConfig(legendCount: number): any {
    const baseConfig = {
      orient: 'horizontal' as const,
      top: '5px',
      left: 'center',
      itemGap: 15,
      itemWidth: 14,
      itemHeight: 14,
      borderColor: '#ccc',
      borderWidth: 0,
      backgroundColor: 'transparent',
      padding: [8, 15],
      textStyle: {
        fontSize: 12,
        color: '#333',
      },
      pageTextStyle: {
        fontSize: 11,
        color: '#666',
      },
      topMargin: '12%', // Default top margin for grid
    };

    if (legendCount <= 3) {
      // Very few items: simple horizontal layout
      return {
        ...baseConfig,
        itemGap: 25,
        topMargin: '8%',
      };
    } else if (legendCount <= 6) {
      // Few items: horizontal layout with better spacing
      return {
        ...baseConfig,
        itemGap: 20,
        topMargin: '10%',
      };
    } else if (legendCount <= 10) {
      // Medium items: scrollable horizontal with navigation
      return {
        ...baseConfig,
        type: 'scroll' as const,
        scrollDataIndex: 0,
        pageButtonItemGap: 12,
        pageButtonGap: 15,
        pageButtonPosition: 'end' as const,
        pageFormatter: function (param: any) {
          return `${param.current} / ${param.total}`;
        },
        pageIcons: {
          horizontal: ['M0,0L16,8L0,16z', 'M16,0L0,8L16,16z'], // Larger icons
        },
        pageIconColor: '#2c3e50',
        pageIconInactiveColor: '#bdc3c7',
        pageIconSize: [16, 16],
        animation: true,
        animationDurationUpdate: 300,
        topMargin: '12%',
      };
    } else {
      // Many items: enhanced scrollable with better navigation
      return {
        ...baseConfig,
        type: 'scroll' as const,
        scrollDataIndex: 0,
        itemGap: 12,
        itemWidth: 12,
        itemHeight: 12,
        textStyle: {
          fontSize: 11,
          color: '#333',
        },
        pageButtonItemGap: 10,
        pageButtonGap: 12,
        pageButtonPosition: 'end' as const,
        pageFormatter: function (param: any) {
          return `Page ${param.current} of ${param.total}`;
        },
        pageIcons: {
          horizontal: [
            'path://M7,0L14,5L7,10L5.3,8.3L9.6,5L5.3,1.7z', // Right arrow, slightly larger path for better visibility

            'path://M9,0L2,5L9,10L10.7,8.3L6.4,5L10.7,1.7z', // Left arrow, slightly larger path
          ],
        },
        pageIconColor: '#34495e',
        pageIconInactiveColor: '#95a5a6',
        pageIconSize: [18, 14],
        pageTextStyle: {
          fontSize: 10,
          color: '#7f8c8d',
          fontWeight: 'normal',
        },
        // Enhanced visual styling
        backgroundColor: 'rgba(248, 249, 250, 0.8)',
        borderColor: '#dee2e6',
        borderWidth: 1,
        borderRadius: 6,
        shadowColor: 'rgba(0,0,0,0.1)',
        shadowBlur: 4,
        shadowOffsetY: 2,
        animation: true,
        animationDurationUpdate: 400,
        animationEasing: 'cubicInOut',
        topMargin: '15%',
      };
    }
  }
}
